import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Button } from '@leadsales/design-system/react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import FormDialog from 'src/components/FormDialog/FormDialog.component';
import {
  closeErrorCopyKey,
  firstName,
  formatMoney,
} from './LeadClose.functions';
import type { tCloseResult } from './LeadClose.types';
import { useLeadClose } from './useLeadClose.hook';
import './LeadCloseDeleteConfirm.styles.scss';

type tLeadCloseDeleteConfirmProps = {
  leadId: string;
  leadName: string;
  result: tCloseResult;
  onClose: () => void;
};

/**
 * "¿Eliminar esta venta?" (U15, prototype `openConfirm`): says what the
 * deletion does to Ganancia — down by the amount, or back to the old amount
 * the sale replaced — or that the lead stops counting as lost/resolved, and
 * that an archived lead stays archived.
 */
export default function LeadCloseDeleteConfirm(
  props: tLeadCloseDeleteConfirmProps,
): JSX.Element {
  const { leadId, leadName, result, onClose } = props;
  const { t } = useTranslation('chat/chat');
  const { removeResult } = useLeadClose();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const kind = result.type === 'sold' ? 'sold' : 'result';
  const name = firstName(leadName);
  const body =
    result.type === 'sold'
      ? result.prior_handling === 'replaced' && result.prior_amount
        ? t('leadClose.delete.body.soldReplaced', {
            name,
            amount: formatMoney(result.prior_amount),
          })
        : t('leadClose.delete.body.sold', {
            name,
            amount: formatMoney(result.amount ?? '0'),
          })
      : t(`leadClose.delete.body.${result.type}`, { name });

  async function confirm() {
    setBusy(true);
    setError(null);
    const refused = await removeResult(leadId, result);
    setBusy(false);
    if (refused) {
      setError(refused);
      return;
    }
    onClose();
  }

  return createPortal(
    <FormDialog
      id="lead-close-delete"
      title={t(`leadClose.delete.title.${kind}`)}
      icon={
        <ExclamationTriangleIcon
          className="LeadCloseDeleteConfirm__Warn"
          width={20}
          height={20}
        />
      }
      busy={busy}
      closeLabel={t('leadClose.form.close')}
      onClose={onClose}
      onSubmit={() => void confirm()}
      actions={
        <>
          <Button
            type="button"
            color="ghost"
            size="sm"
            data-autofocus
            disabled={busy}
            onClick={onClose}
          >
            {t('leadClose.form.cancel')}
          </Button>
          <Button type="submit" color="error" size="sm" loading={busy}>
            {t(`leadClose.delete.confirm.${kind}`)}
          </Button>
        </>
      }
    >
      <p>{body}</p>
      {result.archive_requested ? (
        <p>{t('leadClose.delete.archived')}</p>
      ) : null}
      <p>{t('leadClose.delete.history')}</p>
      {error ? (
        <p className="FormDialog__Error" role="alert">
          {t(closeErrorCopyKey(error, result.type))}
        </p>
      ) : null}
    </FormDialog>,
    document.body,
  );
}
